import { useState } from 'react';
import type { ErrorField } from '../types/ErrorField';
import type { UpdateJudicialDepositPayload } from '../types/LegalCase';

// Campos com binding próprio no formulário de levantamento.
// `abate` entra aqui porque o seletor de operação também o exibe (são as recusas de regra: sem
// levantamento registrado, ou polo passivo). Sem isto a mensagem apareceria duas vezes — no campo
// e no bloco de erros residuais.
const INLINE_FIELDS = new Set([
    'withdrawnAmount', 'withdrawnAt', 'abateExternalReference', 'abateObligationType', 'abate',
]);

export const residualWithdrawalErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !INLINE_FIELDS.has(e.fieldName));

const moneyOrUndefined = (value: number): number | undefined =>
    value && value > 0 ? value : undefined;

interface UseLegalCaseWithdrawalFormArgs {
    onSubmit: (value: UpdateJudicialDepositPayload) => Promise<boolean>;
    // Operação-alvo já escolhida: pré-seleção quando o caso tem **uma só** operação elegível. O
    // form calcula e passa; o hook só guarda o estado.
    initialAbateKey?: string;
}

// Estado e handlers do formulário de levantamento (edição do depósito: valor + data do
// alvará), fora do componente. Valor e data andam juntos — a coerência é validada no backend
// (422), então o botão fica sempre habilitado. Centraliza o reset ao enviar com sucesso.
//
// Abatimento (Task 18): quando o usuário escolhe uma operação-alvo (`abateKey` = "tipo|referência"),
// o levantamento vira um lançamento que reduz a pretensão. Só faz sentido no polo ativo — o form
// nem oferece o seletor fora dele. Em branco, o depósito só informa (comportamento anterior).
export default function useLegalCaseWithdrawalForm({
    onSubmit,
    initialAbateKey = '',
}: UseLegalCaseWithdrawalFormArgs) {
    const [withdrawnAmount, setWithdrawnAmount] = useState(0);
    const [withdrawnAt, setWithdrawnAt] = useState('');
    const [abateKey, setAbateKey] = useState(initialAbateKey);

    const reset = () => {
        setWithdrawnAmount(0);
        setWithdrawnAt('');
        setAbateKey(initialAbateKey);
    };

    const canSubmit = true;

    const submit = async () => {
        // "— não abater" manda **`null`**, não chave omitida: omitir significa "mantém o abatimento
        // atual" no backend, e aqui o usuário escolheu explicitamente não abater. Enviar `undefined`
        // deixaria um abatimento anterior de pé contra a escolha da tela.
        const [obligationType, externalReference] = abateKey.split('|');
        const ok = await onSubmit({
            withdrawnAmount: moneyOrUndefined(withdrawnAmount),
            withdrawnAt: withdrawnAt.trim() || undefined,
            abateObligationType: (obligationType || null) as any,
            abateExternalReference: externalReference || null,
        });
        if (ok) reset();
    };

    return {
        withdrawnAmount, setWithdrawnAmount,
        withdrawnAt, setWithdrawnAt,
        abateKey, setAbateKey,
        canSubmit, submit, reset,
    };
}
